const CART_DRAWER_NAME_MIN_SCALE = 0.65;
const CART_DRAWER_NAME_SIZE_STEP = 0.5;

let cartDrawerNameMeasureContext;

function getCartDrawerNameMeasureContext() {
  if (!cartDrawerNameMeasureContext) {
    const canvas = document.createElement('canvas');
    cartDrawerNameMeasureContext = canvas.getContext('2d');
  }

  return cartDrawerNameMeasureContext;
}

function cartDrawerNameHasOverflow(nameElement, fontSize) {
  const style = getComputedStyle(nameElement);
  const context = getCartDrawerNameMeasureContext();
  context.font = `${style.fontStyle} ${style.fontWeight} ${fontSize}px ${style.fontFamily}`;

  const containerWidth = nameElement.clientWidth;
  if (!containerWidth) return false;

  const words = nameElement.textContent.trim().split(/\s+/).filter(Boolean);
  return words.some((word) => context.measureText(word).width > containerWidth);
}

function cartDrawerNamesOverflowAtSize(names, fontSize) {
  return Array.from(names).some((nameElement) => cartDrawerNameHasOverflow(nameElement, fontSize));
}

function fitCartDrawerProductNames() {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer || drawer.classList.contains('is-empty')) return;

  const names = drawer.querySelectorAll('.cart-item__name');
  if (!names.length) return;

  drawer.style.removeProperty('--cart-drawer-item-name-font-size');
  names.forEach((nameElement) => nameElement.style.removeProperty('font-size'));

  const baseFontSize = parseFloat(getComputedStyle(names[0]).fontSize);
  if (!baseFontSize) return;

  const minFontSize = Math.max(baseFontSize * CART_DRAWER_NAME_MIN_SCALE, 10);
  let fontSize = baseFontSize;

  while (fontSize > minFontSize && cartDrawerNamesOverflowAtSize(names, fontSize)) {
    fontSize -= CART_DRAWER_NAME_SIZE_STEP;
  }

  if (fontSize < baseFontSize - 0.1) {
    drawer.style.setProperty('--cart-drawer-item-name-font-size', `${fontSize}px`);
  }
}

const debouncedFitCartDrawerProductNames =
  typeof debounce === 'function' ? debounce(fitCartDrawerProductNames, 100) : fitCartDrawerProductNames;

function initCartDrawerTitleFitting(drawer) {
  if (!drawer || drawer.dataset.titleFitInit === 'true') return;
  drawer.dataset.titleFitInit = 'true';

  const observeTarget = drawer.querySelector('.drawer__cart-items-wrapper') || drawer.querySelector('.drawer__inner');
  if (observeTarget && typeof ResizeObserver !== 'undefined') {
    const resizeObserver = new ResizeObserver(() => debouncedFitCartDrawerProductNames());
    resizeObserver.observe(observeTarget);
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      requestAnimationFrame(() => fitCartDrawerProductNames());
    });
  }
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    initCartDrawerTitleFitting(this);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      requestAnimationFrame(() => fitCartDrawerProductNames());
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
        fitCartDrawerProductNames();
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
  this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
  this.open();
  requestAnimationFrame(() => fitCartDrawerProductNames());

  if (
    typeof BOLD === 'object' &&
    BOLD.common &&
    BOLD.common.eventEmitter &&
    typeof BOLD.common.eventEmitter.emit === 'function'
  ) {
    BOLD.common.eventEmitter.emit('BOLD_COMMON_cart_loaded');
  }
});
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  onCartUpdate() {
    const result = super.onCartUpdate();

    if (result && typeof result.then === 'function') {
      return result.then(() => {
        requestAnimationFrame(() => fitCartDrawerProductNames());
      });
    }

    return result;
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
